import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

// Custom hook for managing virtualized member list state and optimizations
export function useVirtualizedList(members, dependencies = []) {
    const [sortBy, setSortBy] = useState('joined');
    const [viewMode, setViewMode] = useState('cards');
    const listRef = useRef(null);
    const sortedMembersRef = useRef([]);

    // Debounced sorting to prevent excessive re-computations
    const sortedMembers = useMemo(() => {
        if (!members || members.length === 0) return [];

        const sorted = [...members].sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.display_name.localeCompare(b.display_name);
                case 'name_desc':
                    return b.display_name.localeCompare(a.display_name);
                case 'joined':
                    return new Date(b.member_since || 0) - new Date(a.member_since || 0);
                case 'joined_asc':
                    return new Date(a.member_since || 0) - new Date(b.member_since || 0);
                default:
                    return 0;
            }
        });

        // Cache sorted results for performance
        sortedMembersRef.current = sorted;
        return sorted;
    }, [members, sortBy, ...dependencies]);

    // Optimized scroll to member function
    const scrollToMember = useCallback((memberId) => {
        const memberIndex = sortedMembersRef.current.findIndex(m => m.user_id === memberId);
        if (memberIndex !== -1 && listRef.current) {
            listRef.current.scrollToItem(memberIndex, 'center');
            return true;
        }
        return false;
    }, []);

    // Batch view mode changes to prevent multiple re-renders
    const changeViewMode = useCallback((newMode) => {
        if (newMode !== viewMode) {
            setViewMode(newMode);
            // Small delay to allow component to re-render before resetting scroll position
            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.resetAfterIndex(0);
                }
            }, 0);
        }
    }, [viewMode]);

    // Optimized sort change with immediate feedback
    const changeSortBy = useCallback((newSort) => {
        if (newSort !== sortBy) {
            setSortBy(newSort);
        }
    }, [sortBy]);

    // Get visible range for debugging/optimization
    const getVisibleRange = useCallback(() => {
        if (!listRef.current) return null;
        
        // This would need to be implemented based on react-window's internals
        // For now, return basic info
        return {
            startIndex: 0,
            endIndex: Math.min(10, sortedMembers.length - 1)
        };
    }, [sortedMembers.length]);

    return {
        sortBy,
        viewMode,
        sortedMembers,
        listRef,
        setSortBy: changeSortBy,
        setViewMode: changeViewMode,
        scrollToMember,
        getVisibleRange
    };
}

// Custom hook for managing hover states with debouncing
export function useOptimizedHover(onMemberHover, onMemberHoverOut, delay = 100) {
    const hoverTimeoutRef = useRef(null);
    const currentHoveredRef = useRef(null);

    const handleMemberHover = useCallback((member) => {
        // Clear any existing timeout
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }

        // Don't trigger if it's the same member
        if (currentHoveredRef.current?.user_id === member.user_id) {
            return;
        }

        // Set timeout for hover
        hoverTimeoutRef.current = setTimeout(() => {
            currentHoveredRef.current = member;
            onMemberHover?.(member);
        }, delay);
    }, [onMemberHover, delay]);

    const handleMemberHoverOut = useCallback(() => {
        // Clear any pending hover
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        // Small delay before clearing hover to prevent flickering
        setTimeout(() => {
            currentHoveredRef.current = null;
            onMemberHoverOut?.();
        }, 50);
    }, [onMemberHoverOut]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    return {
        handleMemberHover,
        handleMemberHoverOut
    };
}

// Custom hook for managing favorites state efficiently
export function useOptimizedFavorites(isFavorite, isPendingFavorite, onToggleFavorite) {
    const favoritesCache = useRef(new Map());
    const pendingCache = useRef(new Map());

    // Memoized favorite checker
    const checkIsFavorite = useCallback((userId) => {
        if (favoritesCache.current.has(userId)) {
            return favoritesCache.current.get(userId);
        }
        const result = isFavorite(userId);
        favoritesCache.current.set(userId, result);
        return result;
    }, [isFavorite]);

    // Memoized pending checker
    const checkIsPending = useCallback((userId) => {
        if (pendingCache.current.has(userId)) {
            return pendingCache.current.get(userId);
        }
        const result = isPendingFavorite(userId);
        pendingCache.current.set(userId, result);
        return result;
    }, [isPendingFavorite]);

    // Optimized toggle with cache invalidation
    const handleToggleFavorite = useCallback((userId) => {
        // Invalidate cache for this user
        favoritesCache.current.delete(userId);
        pendingCache.current.delete(userId);
        
        // Trigger the actual toggle
        onToggleFavorite(userId);
    }, [onToggleFavorite]);

    // Clear cache when dependencies change
    useEffect(() => {
        favoritesCache.current.clear();
        pendingCache.current.clear();
    }, [isFavorite, isPendingFavorite]);

    return {
        checkIsFavorite,
        checkIsPending,
        handleToggleFavorite
    };
}
